import { withConversationCopy } from "@/lib/i18n/conversation-copy"
import type { ThreadFact, ThreadQuality, ThreadTone } from "@/lib/conversation/thread-header"
import { capitalize } from "@/lib/utils"

import type {
  Cohort,
  EscalationCategory,
  HumanOutcome,
  SessionOutcome,
  TakeoverReason,
  TopicCategory,
} from "./outcomes"
import { createTranslator, type useTranslations } from "next-intl"
import englishMessages from "@/translations/en/quality-outcomes.json"

const englishTranslator = createTranslator({ locale: "en", messages: englishMessages, namespace: "QualityOutcomes" })


/**
 * How the taxonomy is shown. Separate from `outcomes.ts` so the module the SQL CHECK
 * constraints are mirrored against carries no UI vocabulary — and so a label can change
 * without touching the values Postgres enforces.
 *
 * Client-safe: the badge, the correction dialog and the analytics all read it.
 */

export const OUTCOME_LABELS = getOUTCOME_LABELS()

export function getOUTCOME_LABELS(t: ReturnType<typeof useTranslations<"QualityOutcomes">> = englishTranslator): Record<SessionOutcome, string> {
  return {
  resolved_positive: t("resolved"),
  resolved_neutral: t("assumedResolved"),
  // Not «Escalated»: in the inbox that word already marks the stamped fact on a reply, and the
  // service metrics use it for the agent asking for a person. This verdict is broader — it also
  // covers a colleague stepping in unasked — so it must not read as the same number.
  escalated: t("handedToTeam"),
  unresolved_left: t("leftUnresolved"),
  abandoned: t("abandoned"),
  spam: t("notARequest"),
}
}

/**
 * What each verdict means, in a line — the legends carry it, so a colour never stands unexplained. A paraphrase of
 * the `outcome` rubric in `review/schema.ts`, written for the operator: re-read it whenever that rubric changes.
 */
export const OUTCOME_DEFINITIONS = getOUTCOME_DEFINITIONS()

export function getOUTCOME_DEFINITIONS(t: ReturnType<typeof useTranslations<"QualityOutcomes">> = englishTranslator): Record<SessionOutcome, string> {
  return {
  resolved_positive: t("theCustomerGotWhatTheyCameForAnd"),
  resolved_neutral: t("theConversationStoppedAfterAnAnswerThatCloses"),
  escalated:
    t("itEndedInPeopleSHandsTheAgent"),
  unresolved_left:
    t("theCustomerLeftDissatisfiedWithTheAgentS"),
  abandoned: t("theCustomerNeverGotARealAnswerThe"),
  spam: t("nobodyWasAskingForAnythingAStoryMention"),
}
}

export const OUTCOME_TONES: Record<SessionOutcome, ThreadTone> = {
  resolved_positive: "success",
  resolved_neutral: "success",
  escalated: "info",
  unresolved_left: "warning",
  abandoned: "warning",
  spam: "neutral",
}

/**
 * One colour per outcome wherever outcomes are stacked into a bar, so a segment keeps its colour from chart to chart —
 * and the colour carries how the ending reads: the two resolutions green, one deeper than the other, a handover blue
 * because it is a path and not a failure, an unresolved ending amber, an abandoned one rose. Its own tokens, not
 * `--chart-N`, whose hues are shuffled between the light and the dark theme; each is picked per theme to clear 3:1
 * against the card and the bar's track. Never red against green alone either: the bar's order and its legend say the
 * same thing without colour.
 */
export const OUTCOME_CHART_COLORS: Record<SessionOutcome, string> = {
  resolved_positive: "hsl(var(--outcome-resolved))",
  resolved_neutral: "hsl(var(--outcome-assumed))",
  escalated: "hsl(var(--outcome-handover))",
  unresolved_left: "hsl(var(--outcome-unresolved))",
  abandoned: "hsl(var(--outcome-abandoned))",
  spam: "hsl(var(--muted-foreground))",
}

/** «Resolved» where it is a background rather than a segment — the same green, half as loud. */
export const FADED_RESOLVED = "hsl(var(--outcome-resolved) / 0.5)"

/** Who handled a session is not how it ended, so the split takes the page's own ink, not an outcome's colour. */
export const COHORT_CHART_COLORS: Record<Cohort, string> = {
  aiOnly: "hsl(var(--cohort-strong))",
  teamInvolved: "hsl(var(--cohort-mid))",
  unattended: "hsl(var(--cohort-faint))",
}

/** The same colours where the names match, so «Resolved» looks alike in both charts. */
export const HUMAN_OUTCOME_CHART_COLORS: Record<HumanOutcome, string> = {
  resolved_positive: OUTCOME_CHART_COLORS.resolved_positive,
  resolved_neutral: OUTCOME_CHART_COLORS.resolved_neutral,
  unresolved_left: OUTCOME_CHART_COLORS.unresolved_left,
  unhandled: OUTCOME_CHART_COLORS.abandoned,
}

export const ESCALATION_CATEGORY_LABELS = getESCALATION_CATEGORY_LABELS()

export function getESCALATION_CATEGORY_LABELS(t: ReturnType<typeof useTranslations<"QualityOutcomes">> = englishTranslator): Record<EscalationCategory, string> {
  return {
  knowledge_gap: t("knowledgeGap"),
  out_of_scope_action: t("outsideTheAgentSReach"),
  customer_preference: t("customerAskedForAPerson"),
  emotional: t("emotionalSituation"),
  policy_boundary: t("policyBoundary"),
  other: t("other"),
}
}

export const TAKEOVER_REASON_LABELS = getTAKEOVER_REASON_LABELS()

export function getTAKEOVER_REASON_LABELS(t: ReturnType<typeof useTranslations<"QualityOutcomes">> = englishTranslator): Record<TakeoverReason, string> {
  return {
  ai_answered_poorly: t("aIAnsweredPoorly"),
  customer_frustrated: t("customerWasFrustrated"),
  sales_opportunity: t("salesOpportunity"),
  no_visible_reason: t("noVisibleReason"),
}
}

export const TOPIC_CATEGORY_LABELS = getTOPIC_CATEGORY_LABELS()

export function getTOPIC_CATEGORY_LABELS(t: ReturnType<typeof useTranslations<"QualityOutcomes">> = englishTranslator): Record<TopicCategory, string> {
  return {
  product_information: t("productInformation"),
  product_fit: t("fitAndChoice"),
  pricing_and_payment: t("pricingAndPayment"),
  availability: t("availability"),
  orders_and_delivery: t("ordersAndDelivery"),
  changes_and_returns: t("changesAndReturns"),
  booking: t("booking"),
  account_and_access: t("accountAndAccess"),
  technical_issue: t("technicalIssue"),
  business_inquiry: t("businessInquiry"),
  contact_request: t("contactRequest"),
  other: t("other"),
}
}

/** A question as it reads beside its kind; the kind is left out where the question is named as it. */
export function questionWithKind(label: string, category: TopicCategory, labels: Record<TopicCategory, string> = TOPIC_CATEGORY_LABELS): string {
  const question = capitalize(label)
  const kind = labels[category]
  return question.toLowerCase() === kind.toLowerCase() ? question : `${question} (${kind})`
}

export const HUMAN_OUTCOME_LABELS = getHUMAN_OUTCOME_LABELS()

export function getHUMAN_OUTCOME_LABELS(t: ReturnType<typeof useTranslations<"QualityOutcomes">> = englishTranslator): Record<HumanOutcome, string> {
  return {
  resolved_positive: t("resolved"),
  resolved_neutral: t("assumedResolved"),
  unresolved_left: t("leftUnresolved"),
  unhandled: t("nobodyReplied"),
}
}

/** The team's part is judged on the session's own scale, so the three shared endings read the same. */
export const HUMAN_OUTCOME_DEFINITIONS = getHUMAN_OUTCOME_DEFINITIONS()

export function getHUMAN_OUTCOME_DEFINITIONS(t: ReturnType<typeof useTranslations<"QualityOutcomes">> = englishTranslator): Record<HumanOutcome, string> {
  return {
  resolved_positive: OUTCOME_DEFINITIONS.resolved_positive,
  resolved_neutral: OUTCOME_DEFINITIONS.resolved_neutral,
  unresolved_left: OUTCOME_DEFINITIONS.unresolved_left,
  unhandled: t("nobodyFromYourTeamWroteInTheConversation"),
}
}

/**
 * The session summary as the header renders it — one full-width row, like a call summary,
 * because no pill holds two sentences.
 *
 * Shared so both inboxes show it under the same label: the badge already appears in both, and
 * a summary present in one and absent in the other reads as a missing summary.
 */
export function sessionSummaryFacts(quality: ThreadQuality | null | undefined, summaryLabel?: string): ThreadFact[] {
  if (!quality?.summary) return []
  if (summaryLabel) return [{ label: summaryLabel, value: quality.summary, block: true }]
  return [withConversationCopy({value: quality.summary, block: true}, {label: {key: "factSummary"}})]
}
